import { ReactComponent as AdminIcon } from "../../assets/Admin.svg";
import { ReactComponent as CODIcon } from "../../assets/COD.svg";
import { ReactComponent as ChevronRight } from "../../assets/ChevronRight.svg";
import CollapsedHint from "../CollapsedHint";
import { IconTruck } from "../SectionIcons";
import { ReactComponent as ManifestsIcon } from "../../assets/Manifests.svg";
import { ReactComponent as NDRIcon } from "../../assets/NDR.svg";
import { NavLink } from "react-router-dom";
import React from "react";
import { ReactComponent as ReportsIcon } from "../../assets/Reports.svg";
import { useSelector } from "react-redux";
import { accessorCanSelectClient } from "../../utils/selectedClientStorage";

const ICON_CLASS = "fill-current w-5 h-5 shrink-0";
const STROKE_ICON_CLASS = "w-5 h-5 shrink-0";
const API_DOCS_URL =
  "https://rednile.stoplight.io/docs/shipments/6kdfms3bbhtei-hackle-shipments-overview";

const NAV_ITEM_CLASS = (collapsed, isActive = false) =>
  `relative flex items-center gap-3 w-full no-underline text-base whitespace-nowrap transition-colors ${
    collapsed ? "justify-center px-0 py-2.5" : "px-3 py-2.5"
  } ${
    isActive
      ? "bg-primary-main/10 text-primary-main font-semibold"
      : "text-grey-900 font-medium hover:bg-grey-50"
  }`;

const DashboardIcon = ({ className }) => (
  <svg
    viewBox='0 0 20 20'
    fill='currentColor'
    className={className}
    aria-hidden='true'>
    <rect x='2' y='2' width='7.25' height='7.25' rx='1.25' />
    <rect x='10.75' y='2' width='7.25' height='4.5' rx='1.25' />
    <rect x='10.75' y='8.25' width='7.25' height='9.75' rx='1.25' />
    <rect x='2' y='11' width='7.25' height='7' rx='1.25' />
  </svg>
);

const ApiLogsIcon = ({ className }) => (
  <svg
    viewBox='0 0 20 20'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.2'
    strokeLinecap='round'
    strokeLinejoin='round'
    className={className}
    aria-hidden='true'>
    <rect x='3.5' y='3.5' width='13' height='13' rx='1.5' />
    <path d='M6.5 7.5h7M6.5 10h7M6.5 12.5h4' />
  </svg>
);

const canSeeAdmin = accessor => {
  const groups = accessor.groups.map(group => group.groupName);
  if (accessor.clientId === null) {
    return (
      groups.includes("Developer Administrators") ||
      groups.includes("Super user")
    );
  }
  return groups.includes("admin");
};

const canSeeApiDocs = accessor => {
  const groups = accessor.groups.map(group => group.groupName);
  return (
    groups.includes("Developer Administrators") ||
    groups.includes("Super user")
  );
};

const RoutingIcon = ({ className }) => (
  <svg
    viewBox='0 0 20 20'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.2'
    strokeLinecap='round'
    strokeLinejoin='round'
    className={className}
    aria-hidden='true'>
    <path d='M3.5 10h4l3-5.5h6M7.5 10l3 5.5h6' />
    <path d='M14.5 2.5l2 2-2 2M14.5 13.5l2 2-2 2' />
  </svg>
);

const ApiDocsIcon = ({ className }) => (
  <svg
    viewBox='0 0 20 20'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.2'
    strokeLinecap='round'
    strokeLinejoin='round'
    className={className}
    aria-hidden='true'>
    <path d='M6 3.5h5l3 3V16.5H6z' />
    <path d='M11 3.5V6.5h3' />
    <path d='M8 10h4M8 13h4' />
  </svg>
);

const NavItemContent = ({ item, collapsed, isActive = false }) => {
  const showNew = item.newUntil && Date.now() < new Date(item.newUntil).getTime();
  return (
    <>
      {isActive && (
        <span className='absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-primary-main' />
      )}
      {item.icon}
      {!collapsed && <span className='truncate'>{item.title}</span>}
      {!collapsed && showNew && (
        <span
          className='ml-auto px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wider bg-primary-lightest text-primary-main uppercase leading-none'
          aria-label='New feature'>
          New
        </span>
      )}
      {!collapsed && item.showChevron && !showNew && (
        <ChevronRight className='ml-auto w-4 h-4 shrink-0 fill-current text-grey-300' />
      )}
    </>
  );
};

const NavItem = ({ item, collapsed, onNavigate }) => {
  const link = item.external ? (
    <a
      href={item.endpoint}
      target='_blank'
      rel='noopener noreferrer'
      onClick={() => onNavigate?.()}
      className={NAV_ITEM_CLASS(collapsed)}>
      <NavItemContent item={item} collapsed={collapsed} />
    </a>
  ) : (
    <NavLink
      end={item.endpoint === "/" || item.end}
      to={item.endpoint}
      onClick={() => onNavigate?.()}
      className={({ isActive }) => NAV_ITEM_CLASS(collapsed, isActive)}>
      {({ isActive }) => (
        <NavItemContent item={item} collapsed={collapsed} isActive={isActive} />
      )}
    </NavLink>
  );

  return (
    <CollapsedHint label={item.title} disabled={!collapsed}>
      {link}
    </CollapsedHint>
  );
};

const SectionLabel = ({ children, collapsed }) => {
  if (collapsed) return <div className='h-2' />;
  return (
    <p className='px-3 pt-3 pb-1 text-xs font-semibold tracking-wider uppercase text-grey-600'>
      {children}
    </p>
  );
};

const SideNavList = ({ collapsed = false, onNavigate }) => {
  const accessor = useSelector(state => state.accessor.accessor);
  if (!accessor) return null;

  const adminEnabled = canSeeAdmin(accessor);
  const apiDocsEnabled = canSeeApiDocs(accessor);
  const tatEnabled = accessor.groups.some(group =>
    ["Developer Administrators", "admin", "Super user"].includes(
      group.groupName,
    ),
  );

  const mainItems = [
    {
      title: "Dashboard",
      endpoint: "/",
      icon: <DashboardIcon className={ICON_CLASS} />,
    },
    {
      title: "Shipments",
      endpoint: "/shipments",
      icon: <IconTruck size={20} className={STROKE_ICON_CLASS} />,
    },
    {
      title: "Manifests",
      endpoint: "/manifests",
      icon: <ManifestsIcon className={ICON_CLASS} />,
    },
    {
      title: "Service requests",
      endpoint: "/service-requests",
      icon: <NDRIcon className={ICON_CLASS} />,
    },
    {
      title: "NDRs",
      endpoint: "/ndr",
      icon: <NDRIcon className={ICON_CLASS} />,
    },
    {
      title: "COD Remittance",
      endpoint: "/cod",
      icon: <CODIcon className={ICON_CLASS} />,
    },
    {
      title: "Reports",
      endpoint: "/reports",
      icon: <ReportsIcon className={ICON_CLASS} />,
    },
  ];

  const adminItems = [
    {
      title: "Administration",
      endpoint: "/admin",
      icon: <AdminIcon className={ICON_CLASS} />,
      enabled: adminEnabled,
      showChevron: true,
      end: true, // exact match so /admin/tats doesn't also highlight Administration
    },
    {
      title: "TAT Management",
      endpoint: "/admin/tats",
      icon: <ApiLogsIcon className={STROKE_ICON_CLASS} />,
      enabled: tatEnabled,
      newUntil: "2026-10-30T00:00:00Z",
    },
    {
      title: "API Logs",
      endpoint: "/api-logs",
      icon: <ApiLogsIcon className={STROKE_ICON_CLASS} />,
      enabled: adminEnabled,
      newUntil: "2026-09-09T00:00:00Z",
    },
    {
      title: "Warehouse Configurations",
      endpoint: "/dev-admin/routing-config",
      icon: <RoutingIcon className={STROKE_ICON_CLASS} />,
      enabled: accessorCanSelectClient(accessor),
    },
    {
      title: "API docs",
      endpoint: API_DOCS_URL,
      icon: <ApiDocsIcon className={STROKE_ICON_CLASS} />,
      enabled: apiDocsEnabled,
      external: true,
    },
  ].filter(item => item.enabled);

  return (
    <nav className='py-1 flex flex-col'>
      <SectionLabel collapsed={collapsed}>Main</SectionLabel>
      {mainItems.map(item => (
        <NavItem
          key={item.endpoint}
          item={item}
          collapsed={collapsed}
          onNavigate={onNavigate}
        />
      ))}
      {adminItems.length > 0 && (
        <>
          <div
            className={`mt-2 ${collapsed ? "mx-2" : "mx-3"} border-t border-grey-100`}
          />
          <SectionLabel collapsed={collapsed}>Administration</SectionLabel>
          {adminItems.map(item => (
            <NavItem
              key={item.endpoint}
              item={item}
              collapsed={collapsed}
              onNavigate={onNavigate}
            />
          ))}
        </>
      )}
    </nav>
  );
};

export default SideNavList;
